<template lang="html">
  <sc-flex :is-column="true" :is-full-width="true">
    <!-- Partial: Alert -->
    <TaxReturnAlert>
      <sc-alert type="info">
        <b>Instruction:</b> Please click the <b>Make Payment</b> button to make
        payment.

        <sc-button-group slot="button-group">
          <sc-button
            v-if="!hasNextStep"
            type="ghost"
            @click="$emit('next')"
            icon-right="arrow-right"
            :is-disabled="taxReturn.payment.status !== 'paid'"
          >
            Next
          </sc-button>
        </sc-button-group>
      </sc-alert>
    </TaxReturnAlert>

    <!-- Partial: Content -->
    <v-layout class="full-height-container">
      <div class="vouchers-container">
        <v-expansion-panel :expand="true" v-model="expanded">
          <v-expansion-panel-content v-if="taxReturn.payment" :key="invoice">
            <template v-slot:header>
              <v-layout class="voucher-header justify-space-between">
                <h2><span>Due now</span></h2>

                <v-chip
                  v-if="taxReturn.payment.status === 'paid'"
                  color="success"
                  small
                  class="white--text"
                >
                  Completed
                </v-chip>
              </v-layout>
            </template>

            <v-layout class="voucher-content" column>
              <v-layout justify-space-between>
                <v-layout>
                  <h3 class="text-xs-left payment-name">
                    <span>
                      {{ currentFirm.name }}'s Tax Service
                      <br />
                      <span class="grey--text text--darken-1 body-2">
                        <span v-if="taxReturn.payment.status === 'paid'">
                          Payment was processed successfully!
                        </span>
                        <span
                          v-else-if="taxReturn.payment.status === 'pending'"
                        >
                          Your payment is awaiting processing, which might take
                          a few days. Please check back later.
                        </span>
                        <span v-else>
                          Please make payment to download your tax return.
                        </span>
                      </span>
                    </span>

                    <v-chip
                      v-if="taxReturn.payment.status === 'paid'"
                      color="success"
                      small
                      class="white--text"
                    >
                      Paid
                    </v-chip>
                  </h3>
                </v-layout>

                <div class="voucher-payment">
                  <v-layout align-center>
                    <h3 class="mr-2 font-weight-regular">
                      <span class="body-1 grey--text">Amount to Pay</span><br />
                      <span>{{
                        formatCurrency(taxReturn.payment.amount / 100)
                      }}</span>
                    </h3>
                  </v-layout>
                </div>
              </v-layout>

              <v-layout
                class="mt-2 payment-instruction"
                align-center
                justify-center
              >
                <v-btn
                  v-if="
                    taxReturn.payment.status !== 'paid' &&
                      taxReturn.payment.status !== 'pending'
                  "
                  class="text-none px-3"
                  color="primary"
                  @click="createCheckoutSession"
                  :loading="creatingCheckoutSession"
                >
                  <v-icon class="mr-1">add_card</v-icon>
                  Make Payment
                </v-btn>

                <v-btn
                  class="text-none px-3"
                  color="primary"
                  @click="downloadTaxReturn(false)"
                  :disabled="taxReturn.payment.status !== 'paid'"
                  :loading="downloadingTaxReturn"
                >
                  <v-icon class="mr-1">download</v-icon>
                  Download tax return only
                </v-btn>

                <v-btn
                  class="text-none px-3 primary--text"
                  outline
                  @click="downloadTaxReturn(true)"
                  :disabled="taxReturn.payment.status !== 'paid'"
                  :loading="
                    downloadingTaxReturnWithSignaturesAndPaymentVouchers
                  "
                >
                  <v-icon class="mr-1">download</v-icon>
                  Download tax return with signatures and payment vouchers
                </v-btn>
              </v-layout>
            </v-layout>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-spacer style="height: 40px" />
      </div>
    </v-layout>
  </sc-flex>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import API from '@/api';
import { mapGetters } from 'vuex';
import UtilsMixins from '@/mixins/UtilsMixins';
import TaxReturnAlert from './components/TaxReturnAlert';

export default {
  mixins: [UtilsMixins],

  components: {
    TaxReturnAlert,
  },

  props: {
    companyId: {
      type: String,
      default: null,
    },

    taxReturn: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      // For stripe payment
      creatingCheckoutSession: false,

      downloadingTaxReturn: false,

      expanded: ['invoice'],

      downloadingTaxReturnWithSignaturesAndPaymentVouchers: false,
    };
  },

  methods: {
    isEmpty,

    formatCurrency(amount) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });

      return formatter.format(amount);
    },

    async createCheckoutSession() {
      try {
        this.creatingCheckoutSession = true;
        const response = await API.taxReturn.createCheckoutSession({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
        });

        window.location = response.data.checkout_url;
      } catch (error) {
        console.log(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem creating the checkout session.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.creatingCheckoutSession = false;
      }
    },

    async downloadTaxReturn(withSignaturesAndPaymentVouchers = false) {
      try {
        if (withSignaturesAndPaymentVouchers) {
          this.downloadingTaxReturnWithSignaturesAndPaymentVouchers = true;
        } else {
          this.downloadingTaxReturn = true;
        }

        const response = await API.taxReturn.downloadTaxReturn({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
          includeOtherPages: withSignaturesAndPaymentVouchers,
        });

        this.downloadDocumentByLink(response.data.link);
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading tax return.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        if (withSignaturesAndPaymentVouchers) {
          this.downloadingTaxReturnWithSignaturesAndPaymentVouchers = false;
        } else {
          this.downloadingTaxReturn = false;
        }
      }
    },
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
    }),
  },

  watch: {},

  async created() {},
};
</script>

<style lang="postcss" scoped>
.sc-alert {
  position: sticky;
  top: 0;

  margin: 0px;
  padding: 8px;

  background-color: var(--blue-600);
  color: var(--gray-0);
}

.full-height-container {
  overflow: auto;
  min-height: calc(100vh - 72px - 52px);
  max-height: calc(100vh - 72px - 52px);
  background-color: #f0f0f0;
  max-width: 100%;
}

.vouchers-container {
  flex: auto;
  min-width: 0;
  max-width: 1000px;
  margin: 0 auto;
  padding: 16px;
}

.v-expansion-panel {
  box-shadow: none;
}

.v-expansion-panel__container {
  border: 1px solid #ccc;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  margin: 12px 0;
  border-radius: 8px !important;
  overflow: hidden;
}

.v-expansion-panel__container {
  & >>> .v-expansion-panel__header {
    background-color: #f7f7f7;
    border-bottom: 1px solid #ccc;
  }
}

.voucher-header {
  padding: 8px 0px;
  font-size: 14px;
}

.voucher-content {
  padding: 12px 14px;
}

.voucher-payment {
  display: flex;
  flex-direction: column;
  align-items: end;
}

.payment-name {
  font-size: 17px;
  font-weight: normal;
}

.payment-instruction {
  display: flex;
  flex-direction: row !important;
}

@media (max-width: 640px) {
  .payment-instruction {
    gap: 8px;
    flex-direction: column !important;
  }
}
</style>
